import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { ArrowLeft, Send, Loader2, Sparkles, Copy, Check, Trash2, Target, MessageSquare, User as UserIcon, Bot } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
    reply: { label: "Reply", icon: MessageSquare, cls: "bg-signal/10 text-signal border-signal/30" },
    outreach: { label: "Outreach", icon: Target, cls: "bg-purple-500/10 text-purple-300 border-purple-500/30" },
    coach: { label: "Coach", icon: Sparkles, cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
};
const METHOD_LABELS = { nepq: "NEPQ", spin: "SPIN", challenger: "Challenger", sandler: "Sandler", meddic: "MEDDIC", gap: "Gap Selling", consultative: "Consultative", straight_line: "Straight Line" };

const SUGGESTIONS = [
    "Make it shorter — under 40 words.",
    "Rewrite ending as a calibrated question.",
    "Make it more direct and less polite.",
    "Add a take-away frame.",
    "Now write a Day-4 bump if they go silent.",
];

function MessageBubble({ msg, onCopy, copied }) {
    const isUser = msg.role === "user";
    return (
        <div data-testid={`chat-message-${msg.id}`} className={`flex ${isUser ? "justify-end" : "justify-start"} group`}>
            <div className={`max-w-[88%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-1`}>
                <div className={`text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 ${isUser ? "text-zinc-500" : "text-signal"} px-1`}>
                    {isUser ? <UserIcon className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                    {isUser ? "You" : "SalesWhisper"}
                    <span className="text-zinc-700">· {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div className={`rounded-lg px-4 py-3 relative ${isUser ? "bg-signal/10 border border-signal/25" : "bg-zinc-900/70 border border-zinc-800"}`}>
                    <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                    {!isUser && msg.content && (
                        <button
                            onClick={() => onCopy(msg.content, msg.id)}
                            data-testid={`copy-chat-msg-${msg.id}`}
                            className="absolute -top-2 -right-2 bg-zinc-950 border border-zinc-800 rounded-md p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:border-signal"
                            title="Copy message"
                        >
                            {copied === msg.id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-zinc-400" />}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function Chat() {
    const { id } = useParams();
    const { authHeaders } = useAuth();
    const navigate = useNavigate();
    const [conv, setConv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(null);
    const scrollRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/conversations/${id}`, { headers: authHeaders() });
            setConv(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load chat");
        } finally { setLoading(false); }
    }, [authHeaders, id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [conv?.messages?.length]);

    const send = async () => {
        const text = input.trim();
        if (!text) return;
        setSending(true);
        try {
            const res = await axios.post(
                `${API}/conversations/${id}/message`,
                { content: text },
                { headers: authHeaders() }
            );
            setConv(res.data);
            setInput("");
        } catch (err) {
            const s = err?.response?.status;
            if (s === 402) {
                toast.error(err?.response?.data?.detail || "Plan limit reached — upgrade for more generations.", { duration: 8000 });
            } else {
                toast.error(err?.response?.data?.detail || "Send failed");
            }
        } finally { setSending(false); }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
    };

    const copy = async (text, key) => {
        try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1400); }
        catch { toast.error("Copy failed"); }
    };

    const delConv = async () => {
        if (!window.confirm("Delete this entire conversation? This cannot be undone.")) return;
        try {
            await axios.delete(`${API}/conversations/${id}`, { headers: authHeaders() });
            toast.success("Conversation deleted");
            navigate("/app/history");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    if (loading) return <div className="text-zinc-500" data-testid="chat-loading">Loading conversation…</div>;
    if (!conv) return null;

    const kind = KIND_META[conv.kind] || KIND_META.reply;
    const KindIcon = kind.icon;
    const methodLabel = conv.methodology ? METHOD_LABELS[conv.methodology] || conv.methodology : null;

    return (
        <div data-testid="chat-page" className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-8rem)]">
            {/* Header */}
            <div className="mb-4 shrink-0">
                <Link to="/app/history" data-testid="back-to-history" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white mb-3">
                    <ArrowLeft className="w-3.5 h-3.5" /> All conversations
                </Link>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap mb-1.5">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-mono uppercase tracking-widest border ${kind.cls}`} data-testid="chat-kind-badge">
                                <KindIcon className="w-3 h-3" /> {kind.label}
                            </span>
                            {methodLabel && (
                                <span className="text-[10px] font-mono uppercase tracking-widest text-signal" data-testid="chat-methodology">{methodLabel}</span>
                            )}
                            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{conv.tone} · {conv.industry} · {conv.platform}</span>
                        </div>
                        <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight truncate" data-testid="chat-title">{conv.title || "Conversation"}</h1>
                    </div>
                    <button
                        onClick={delConv}
                        data-testid="delete-chat-btn"
                        className="text-xs text-zinc-500 hover:text-red-400 inline-flex items-center gap-1.5"
                    >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 py-2 pr-1" data-testid="chat-messages">
                {(conv.messages || []).map((m) => (
                    <MessageBubble key={m.id} msg={m} onCopy={copy} copied={copied} />
                ))}
                {sending && (
                    <div className="flex justify-start" data-testid="chat-typing">
                        <div className="bg-zinc-900/70 border border-zinc-800 rounded-lg px-4 py-3">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-signal flex items-center gap-1.5 mb-1.5"><Bot className="w-3 h-3" /> SalesWhisper</div>
                            <div className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Composer */}
            <div className="shrink-0 mt-4 border border-zinc-900 bg-[#0c0c0c] rounded-md p-3" data-testid="chat-composer">
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={2}
                    placeholder="Ask SalesWhisper to refine — e.g. 'shorter and more direct', 'add a take-away', 'rewrite for a CFO'…"
                    data-testid="chat-input"
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal resize-none"
                    disabled={sending}
                />
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    {SUGGESTIONS.map((s) => (
                        <button
                            key={s}
                            onClick={() => setInput(s)}
                            className="text-[10px] text-zinc-500 hover:text-signal border border-zinc-800 hover:border-signal/40 rounded px-2 py-1 transition-colors"
                            data-testid={`suggestion-${s.slice(0, 12)}`}
                        >
                            {s}
                        </button>
                    ))}
                    <button
                        onClick={send}
                        disabled={sending || !input.trim()}
                        data-testid="chat-send-btn"
                        className="ml-auto bg-signal hover:bg-signal-hover text-white px-4 py-2 rounded-md font-semibold text-sm flex items-center gap-2 transition-colors disabled:opacity-60"
                    >
                        {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : <><Send className="w-4 h-4" /> Send <span className="text-[10px] text-white/70 hidden md:inline">⌘+↵</span></>}
                    </button>
                </div>
            </div>
        </div>
    );
}
